import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, StarIcon } from '@heroicons/react/24/outline';
import KeyBenefitCard from '../components/KeyBenefitCard';
import KeyBenefitModal from '../components/KeyBenefitModal';
import ConfirmDialog from '../components/ConfirmDialog';

const TopUpLoanKeyBenefitsPage = () => {
    const [benefits, setBenefits] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Key Benefits & Features',
        section_subtitle: 'Discover why our top-up loan is the right choice for you'
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBenefit, setEditingBenefit] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, benefitId: null });

    const LOAN_TYPE = 'top_up_loan';

    // Fetch benefits and settings
    const fetchData = async () => {
        try {
            setLoading(true);
            const [benefitsRes, settingsRes] = await Promise.all([
                axios.get(`/api/admin/key-benefits/${LOAN_TYPE}`),
                axios.get(`/api/admin/key-benefits/settings/${LOAN_TYPE}`)
            ]);

            setBenefits(benefitsRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load key benefits');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingBenefit) {
                await axios.put(`/api/admin/key-benefits/${editingBenefit.id}`, formData);
                toast.success('Benefit updated successfully');
            } else {
                await axios.post('/api/admin/key-benefits', formData);
                toast.success('Benefit created successfully');
            }
            setIsModalOpen(false);
            setEditingBenefit(null);
            fetchData();
        } catch (error) {
            console.error('Error saving benefit:', error);
            toast.error('Failed to save benefit');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, benefitId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/key-benefits/${deleteConfirm.benefitId}`);
            toast.success('Benefit deleted successfully');
            setDeleteConfirm({ isOpen: false, benefitId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting benefit:', error);
            toast.error('Failed to delete benefit');
        }
    };

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put(`/api/admin/key-benefits/settings/${LOAN_TYPE}`, settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Top-up Loan - Key Benefits</h1>
                    <p className="text-slate-500 mt-1">Manage key benefits and features</p>
                </div>
                <button
                    onClick={() => {
                        setEditingBenefit(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add Benefit
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
                <div className="flex items-center gap-2 mb-4">
                    <StarIcon className="h-5 w-5 text-yellow-500" />
                    <h2 className="text-lg font-bold text-slate-800">Section Settings</h2>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="e.g., Key Benefits & Features"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                            placeholder="e.g., Discover why our loan is the right choice"
                        />
                    </div>

                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 rounded-xl font-semibold transition-all disabled:opacity-50"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* Benefits Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl p-6 shadow-sm animate-pulse">
                            <div className="w-12 h-12 bg-slate-200 rounded-xl mb-4"></div>
                            <div className="h-6 bg-slate-200 rounded mb-2"></div>
                            <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                        </div>
                    ))}
                </div>
            ) : benefits.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <StarIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-800 mb-2">No benefits yet</h3>
                        <p className="text-slate-500 mb-6">
                            Add key benefits to showcase the advantages of your top-up loan
                        </p>
                        <button
                            onClick={() => {
                                setEditingBenefit(null);
                                setIsModalOpen(true);
                            }}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200"
                        >
                            Add First Benefit
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {benefits.map((benefit) => (
                        <KeyBenefitCard
                            key={benefit.id}
                            benefit={benefit}
                            onEdit={(benefit) => {
                                setEditingBenefit(benefit);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Benefit Modal */}
            {isModalOpen && (
                <KeyBenefitModal
                    isOpen={isModalOpen}
                    benefit={editingBenefit}
                    loanType={LOAN_TYPE}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingBenefit(null);
                    }}
                    onSubmit={handleSubmit}
                    loading={submitting}
                />
            )}

            {/* Delete Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, benefitId: null })}
                onConfirm={confirmDelete}
                title="Delete Benefit"
                message="Are you sure you want to delete this benefit? This action cannot be undone."
            />
        </div>
    );
};

export default TopUpLoanKeyBenefitsPage;
